
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { RichTextEditor } from './RichTextEditor';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const formSchema = z.object({
  slug: z.string().min(1, "URL slug is required").regex(/^[a-z0-9-]+$/, "URL slug can only contain lowercase letters, numbers, and hyphens"),
  title: z.string().min(1, "Title is required"),
  metaTitle: z.string().min(1, "Meta title is required").max(60, "Meta title should be 60 characters or less"),
  metaDescription: z.string().min(1, "Meta description is required").max(160, "Meta description should be 160 characters or less"),
  content: z.string().min(1, "Content is required"),
  imageUrl: z.string().optional(),
  published: z.boolean(),
});

interface SeoPageFormProps {
  initialData?: any;
  onSuccess: () => void;
}

export const SeoPageForm = ({ initialData, onSuccess }: SeoPageFormProps) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("content");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(initialData?.image_url || null);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      slug: initialData?.slug || '',
      title: initialData?.title || '',
      metaTitle: initialData?.meta_title || '',
      metaDescription: initialData?.meta_description || '',
      content: initialData?.content || '<p>Enter your content here...</p>',
      imageUrl: initialData?.image_url || '',
      published: initialData?.published || false,
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    try {
      // Handle image upload if there's a new image
      let imageUrl = values.imageUrl;
      if (imageFile) {
        // Check if images bucket exists, if not create it
        const { data: buckets } = await supabase.storage.listBuckets();
        if (!buckets?.find(bucket => bucket.name === 'images')) {
          await supabase.storage.createBucket('images', { public: true });
        }
        
        // Generate a unique file name
        const fileExt = imageFile.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now().toString(36)}.${fileExt}`;
        
        // Upload the image
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('images')
          .upload(`seo/${fileName}`, imageFile, { upsert: false });
          
        if (uploadError) throw uploadError;
        
        // Get the public URL
        const { data: publicUrlData } = supabase.storage
          .from('images')
          .getPublicUrl(`seo/${fileName}`);
        
        if (publicUrlData && publicUrlData.publicUrl) {
          imageUrl = publicUrlData.publicUrl;
        }
      }

      const { error } = initialData 
        ? await supabase
            .from('seo_pages')
            .update({
              slug: values.slug,
              title: values.title,
              meta_title: values.metaTitle,
              meta_description: values.metaDescription,
              content: values.content,
              image_url: imageUrl,
              published: values.published,
            })
            .eq('id', initialData.id)
        : await supabase
            .from('seo_pages')
            .insert({
              slug: values.slug,
              title: values.title,
              meta_title: values.metaTitle,
              meta_description: values.metaDescription,
              content: values.content,
              image_url: imageUrl,
              published: values.published,
            });

      if (error) throw error;

      toast({
        title: `Page ${initialData ? 'updated' : 'created'} successfully`,
        description: `The page "${values.title}" has been ${initialData ? 'updated' : 'created'}.`,
      });

      onSuccess();
    } catch (error) {
      console.error('Error saving page:', error);
      toast({
        title: "Error saving page",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    form.setValue('imageUrl', '');
    setImageFile(null);
    setImagePreview(null);
  };

  // Calculate character counts
  const metaTitleLength = form.watch('metaTitle').length;
  const metaDescriptionLength = form.watch('metaDescription').length;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full">
            <TabsTrigger value="content" className="flex-1">Content</TabsTrigger>
            <TabsTrigger value="seo" className="flex-1">SEO</TabsTrigger>
            <TabsTrigger value="media" className="flex-1">Media</TabsTrigger>
            <TabsTrigger value="settings" className="flex-1">Settings</TabsTrigger>
          </TabsList>
          
          <TabsContent value="content" className="space-y-6 pt-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Page Title (H1)</FormLabel>
                  <FormControl>
                    <Input placeholder="Best Family Cell Phone Plans 2025" {...field} />
                  </FormControl>
                  <FormDescription>
                    This is the main title that will appear on the page.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Content</FormLabel>
                  <FormControl>
                    <RichTextEditor content={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TabsContent>
          
          <TabsContent value="seo" className="space-y-6 pt-4">
            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL Slug</FormLabel>
                  <FormControl>
                    <Input placeholder="best-family-plans-2025" {...field} />
                  </FormControl>
                  <FormDescription>
                    The URL path for this page (e.g., /best-family-plans-2025)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="metaTitle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Meta Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Best Family Cell Phone Plans 2025 | Compare & Save" {...field} />
                  </FormControl>
                  <FormDescription className={metaTitleLength > 50 && metaTitleLength <= 60 ? "text-yellow-500" : metaTitleLength > 60 ? "text-red-500" : ""}>
                    {metaTitleLength}/60 characters. Appears in search results and browser tabs.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="metaDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Meta Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Compare the best family cell phone plans for 2025. Find deals on unlimited data, multiple lines, and shared plans from top providers."
                      {...field}
                    />
                  </FormControl>
                  <FormDescription className={metaDescriptionLength > 140 && metaDescriptionLength <= 160 ? "text-yellow-500" : metaDescriptionLength > 160 ? "text-red-500" : ""}>
                    {metaDescriptionLength}/160 characters. Appears in search results below the title.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TabsContent>
          
          <TabsContent value="media" className="space-y-6 pt-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="feature-image">Featured Image</Label>
                <div className="mt-2">
                  <Input
                    id="feature-image"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </div>
              </div>
              
              {(imagePreview || form.watch('imageUrl')) && (
                <div className="border rounded-md p-2">
                  <div className="mb-2">Current image:</div>
                  <div className="relative w-full aspect-video bg-muted/20 rounded-md overflow-hidden">
                    <img 
                      src={imagePreview || form.watch('imageUrl')}
                      alt="Featured image preview"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    className="mt-2" 
                    type="button"
                    onClick={handleRemoveImage}
                  >
                    Remove Image
                  </Button>
                </div>
              )}
              
              <FormField
                control={form.control}
                name="imageUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Image URL (Optional)</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="https://example.com/image.jpg" 
                        value={field.value || ''} 
                        onChange={field.onChange}
                        disabled={!!imageFile} 
                      />
                    </FormControl>
                    <FormDescription>
                      If you don't upload an image, you can specify an external URL instead.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>
          </TabsContent>
          
          <TabsContent value="settings" className="space-y-6 pt-4">
            <FormField
              control={form.control}
              name="published"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between">
                  <div>
                    <FormLabel className="mb-0">Published</FormLabel>
                    <FormDescription>
                      When enabled, the page will be visible to visitors.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </TabsContent>
        </Tabs>

        <div className="pt-4 border-t flex justify-end">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : initialData ? 'Update Page' : 'Create Page'}
          </Button>
        </div>
      </form>
    </Form>
  );
};
